import React, { useEffect, useMemo, useCallback, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Grid,
  Divider,
  Tabs,
  Tab,
  TextField,
  Button,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Checkbox,
  Avatar,
  IconButton,
  Tooltip,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
import { alpha } from "@mui/material/styles";

import SendIcon from "@mui/icons-material/Send";
import PeopleIcon from "@mui/icons-material/People";
import MessageIcon from "@mui/icons-material/Message";
import NotificationsIcon from "@mui/icons-material/Notifications";
import SmsIcon from "@mui/icons-material/Sms";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import WifiIcon from "@mui/icons-material/Wifi";
import WifiOffIcon from "@mui/icons-material/WifiOff";

import { useSocket } from "../../../hooks/useSocket";

const API_BASE =
  process.env.REACT_APP_BACKEND_URL ||
  process.env.REACT_APP_API_URL ||
  "https://qylmox.in/api";

const BROADCAST_ROLES = [
  { value: "all", label: "All Users (Except Admins)", canHaveSubordinates: false },
  { value: "manager", label: "Managers", canHaveSubordinates: true },
  { value: "hr", label: "HR Personnel", canHaveSubordinates: false },
  { value: "sub_admin", label: "Admins", canHaveSubordinates: true },
  { value: "dealer", label: "Dealers", canHaveSubordinates: true },
  { value: "group_admin", label: "Dealers", canHaveSubordinates: true },
  { value: "user", label: "Standard Users", canHaveSubordinates: false },
  { value: "client", label: "Clients", canHaveSubordinates: false },
  { value: "field_emp", label: "Field Employees", canHaveSubordinates: false },
  { value: "office_emp", label: "Office Employees", canHaveSubordinates: false },
];

const DELIVERY = [
  { value: "Notification", label: "In-App Notification", icon: <NotificationsIcon fontSize="small" /> },
  { value: "SMS", label: "SMS", icon: <SmsIcon fontSize="small" /> },
];

const NAV_THEME_BG = "#0f172a";
const NAV_THEME_PANEL = "#152136";
const NAV_THEME_PANEL_ALT = "#1a2742";
const NAV_THEME_BORDER = "rgba(148, 163, 184, 0.22)";
const NAV_THEME_TEXT = "#f8fafc";
const NAV_THEME_MUTED = "rgba(226, 232, 240, 0.75)";
const NAV_THEME_ACCENT = "#4ea1ff";
const NAV_THEME_SUCCESS = "#2f8f46";

const safeArr = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.users)) return data.users;
  return [];
};

const fmtDateTime = (dateString) => {
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// ✅ Token helper (Bearer)
const getAuthToken = () => localStorage.getItem("token") || localStorage.getItem("authToken");

function RecipientTabPanel({ value, index, children }) {
  return (
    <Box role="tabpanel" hidden={value !== index} sx={{ pt: 2 }}>
      {value === index && children}
    </Box>
  );
}

export default function MessageManagement() {
  const { socket, connected } = useSocket();
  const currentUser = useSelector((state) => state.auth?.user);
  const currentRole = String(currentUser?.role || "").trim().toLowerCase();
  const isSubAdmin = currentRole === "sub_admin";
  const isDealer = currentRole === "group_admin" || currentRole === "dealer";
  const isScopedSender = isSubAdmin || isDealer;

  // Tabs: 0 Role, 1 Individuals, 2 Subordinates
  const [tab, setTab] = useState(() => {
    if (isDealer) return 1;
    if (isSubAdmin) return 1;
    return 0;
  });
  const [deliveryType, setDeliveryType] = useState("Notification");

  // Role broadcast
  const [selectedRole, setSelectedRole] = useState(() => {
    if (isSubAdmin) return "group_admin";
    return "all";
  });

  // Individuals (MULTI)
  const [allUsers, setAllUsers] = useState([]);
  const [individualSearch, setIndividualSearch] = useState("");
  const [selectedIndividuals, setSelectedIndividuals] = useState([]);

  // Subordinates flow
  const [roleUsers, setRoleUsers] = useState([]);
  const [roleUserSearch, setRoleUserSearch] = useState("");
  const [selectedRoleUser, setSelectedRoleUser] = useState(null);

  const [subordinateUsers, setSubordinateUsers] = useState([]);
  const [subSearch, setSubSearch] = useState("");
  const [selectedSubordinates, setSelectedSubordinates] = useState([]);

  // Message + history
  const [message, setMessage] = useState("");
  const [messageHistory, setMessageHistory] = useState([]);

  // Loading
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingRoleUsers, setLoadingRoleUsers] = useState(false);
  const [loadingSubs, setLoadingSubs] = useState(false);
  const [sending, setSending] = useState(false);

  // Snackbar
  const [snack, setSnack] = useState({ open: false, severity: "success", msg: "" });
  const notify = useCallback((severity, msg) => setSnack({ open: true, severity, msg }), []);

  const panelSx = useMemo(
    () => ({
      borderRadius: 4,
      overflow: "hidden",
      bgcolor: alpha(NAV_THEME_PANEL, 0.96),
      border: `1px solid ${NAV_THEME_BORDER}`,
      boxShadow: "0 22px 44px rgba(15, 23, 42, 0.16)",
      color: NAV_THEME_TEXT,
    }),
    []
  );

  const formShellSx = useMemo(
    () => ({
      bgcolor: alpha("#ffffff", 0.04),
      border: `1px solid ${NAV_THEME_BORDER}`,
      borderRadius: 3,
      "& .MuiOutlinedInput-root": {
        borderRadius: 2.5,
        backgroundColor: alpha("#ffffff", 0.03),
        color: NAV_THEME_TEXT,
        "& fieldset": { borderColor: NAV_THEME_BORDER },
        "&:hover fieldset": { borderColor: alpha(NAV_THEME_ACCENT, 0.6) },
        "&.Mui-focused fieldset": { borderColor: NAV_THEME_ACCENT },
      },
      "& .MuiInputLabel-root": { color: NAV_THEME_MUTED },
      "& .MuiInputLabel-root.Mui-focused": { color: NAV_THEME_ACCENT },
      "& .MuiFormHelperText-root": { color: NAV_THEME_MUTED },
      "& .MuiSvgIcon-root": { color: NAV_THEME_MUTED },
      "& .MuiSelect-select": { color: NAV_THEME_TEXT },
      "& .MuiAutocomplete-tag": {
        bgcolor: alpha(NAV_THEME_ACCENT, 0.15),
        color: NAV_THEME_TEXT,
        border: `1px solid ${alpha(NAV_THEME_ACCENT, 0.28)}`,
      },
    }),
    []
  );

  // Socket optional
  useEffect(() => {
    if (!socket) return;
    return () => {};
  }, [socket]);

  // ✅ Fetch users list from: GET /api/admin/users
  const fetchUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const token = getAuthToken();
      if (!token) {
        notify("warning", "Token missing. Please login again.");
        return;
      }

      const { data } = await axios.get(`${API_BASE}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setAllUsers(safeArr(data));
    } catch (e) {
      console.error(e);
      notify("error", e?.response?.data?.msg || "Failed to fetch users.");
    } finally {
      setLoadingUsers(false);
    }
  }, [notify]);

  // ✅ Fetch history from: GET /api/messaging/history
  const fetchHistory = useCallback(async () => {
    try {
      const token = getAuthToken();
      if (!token) return;

      const { data } = await axios.get(`${API_BASE}/messaging/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setMessageHistory(safeArr(data));
    } catch (e) {
      console.error(e);
      // history not critical
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchHistory();
  }, [fetchUsers, fetchHistory]);

  // Subordinates mode config
  const selectedRoleConfig = useMemo(
    () => BROADCAST_ROLES.find((r) => r.value === selectedRole),
    [selectedRole]
  );

  const availableTabs = useMemo(() => {
    if (isDealer) return [1];
    if (isSubAdmin) return [1, 2];
    return [0, 1, 2];
  }, [isDealer, isSubAdmin]);

  const availableRoleOptions = useMemo(() => {
    if (isDealer) return [];
    if (isSubAdmin) return BROADCAST_ROLES.filter((role) => role.value === "group_admin");
    return BROADCAST_ROLES;
  }, [isDealer, isSubAdmin]);

  useEffect(() => {
    if (!availableTabs.includes(tab)) {
      setTab(availableTabs[0] || 1);
    }
  }, [availableTabs, tab]);

  useEffect(() => {
    if (!availableRoleOptions.length) return;
    if (!availableRoleOptions.some((role) => role.value === selectedRole)) {
      setSelectedRole(availableRoleOptions[0].value);
    }
  }, [availableRoleOptions, selectedRole]);

  // ✅ Fetch role users list (parent users) for hierarchy selection
  // NOTE: aapke code me endpoint: /users/by-role/:role
  // ensure backend has it with /api prefix: /api/users/by-role/:role
  useEffect(() => {
    if (tab !== 2) return;

    if (!selectedRoleConfig?.canHaveSubordinates) {
      setRoleUsers([]);
      setSelectedRoleUser(null);
      setSubordinateUsers([]);
      setSelectedSubordinates([]);
      return;
    }

    const run = async () => {
      setLoadingRoleUsers(true);
      try {
        const token = getAuthToken();
        if (!token) return;

        const { data } = await axios.get(`${API_BASE}/users/by-role/${selectedRole}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setRoleUsers(safeArr(data));
      } catch (e) {
        console.error(e);
        notify("error", `Failed to fetch ${selectedRoleConfig.label} list.`);
        setRoleUsers([]);
      } finally {
        setLoadingRoleUsers(false);
      }
    };

    run();
  }, [tab, selectedRole, selectedRoleConfig, notify]);

  // ✅ Fetch subordinates: GET /api/messaging/subordinates/:userId
  useEffect(() => {
    if (tab !== 2) return;

    if (!selectedRoleUser?._id) {
      setSubordinateUsers([]);
      setSelectedSubordinates([]);
      return;
    }

    const run = async () => {
      setLoadingSubs(true);
      try {
        const token = getAuthToken();
        if (!token) return;

        const { data } = await axios.get(`${API_BASE}/messaging/subordinates/${selectedRoleUser._id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setSubordinateUsers(safeArr(data));
      } catch (e) {
        console.error(e);
        notify("error", "Failed to fetch subordinate users.");
        setSubordinateUsers([]);
      } finally {
        setLoadingSubs(false);
      }
    };

    run();
  }, [tab, selectedRoleUser, notify]);

  // Filters
  const individualOptions = useMemo(() => {
    const q = individualSearch.trim().toLowerCase();
    if (!q) return allUsers.slice(0, 80);
    return allUsers
      .filter((u) => (u.username || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q))
      .slice(0, 80);
  }, [allUsers, individualSearch]);

  const roleUserOptions = useMemo(() => {
    const q = roleUserSearch.trim().toLowerCase();
    if (!q) return roleUsers;
    return roleUsers.filter(
      (u) => (u.username || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q)
    );
  }, [roleUsers, roleUserSearch]);

  const filteredSubs = useMemo(() => {
    const q = subSearch.trim().toLowerCase();
    if (!q) return subordinateUsers;
    return subordinateUsers.filter(
      (u) => (u.username || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q)
    );
  }, [subordinateUsers, subSearch]);

  const selectAllSubordinates = () => setSelectedSubordinates([...filteredSubs]);
  const clearAllSubordinates = () => setSelectedSubordinates([]);

  // ✅ Build payload as per backend
  // Backend supports:
  // role => {type:'role', value:'all'|'Manager'...}
  // individual => {type:'individual', value:userId}
  // subordinates => {type:'subordinates', parentUserId, userIds:[]}
  const recipientPayload = useMemo(() => {
    if (tab === 0) {
      return {
        ok: !!selectedRole,
        recipient: { type: "role", value: selectedRole },
        label: availableRoleOptions.find((r) => r.value === selectedRole)?.label || selectedRole,
      };
    }

    if (tab === 1) {
      const ids = selectedIndividuals.map((u) => u._id).filter(Boolean);
      if (ids.length === 1) {
        return { ok: true, recipient: { type: "individual", value: ids[0] }, label: selectedIndividuals[0]?.username };
      }
      if (ids.length > 1) {
        // ✅ MULTI to backend using subordinates
        // NOTE: parentUserId optional fix recommended in backend (below)
        return {
          ok: true,
          recipient: { type: "subordinates", parentUserId: null, userIds: ids },
          label: `${ids.length} selected users`,
        };
      }
      return { ok: false, recipient: null, label: "" };
    }

    if (tab === 2) {
      const ids = selectedSubordinates.map((u) => u._id).filter(Boolean);
      return {
        ok: !!selectedRoleUser?._id && ids.length > 0,
        recipient: { type: "subordinates", parentUserId: selectedRoleUser?._id, userIds: ids },
        label: `${ids.length} users under ${selectedRoleUser?.username || ""}`,
      };
    }

    return { ok: false };
  }, [tab, selectedRole, selectedIndividuals, selectedSubordinates, selectedRoleUser, availableRoleOptions]);

  const isFormValid = Boolean(message.trim()) && recipientPayload.ok;

  // ✅ Send message: POST /api/messaging/send
  const handleSend = async () => {
    if (!message.trim()) return notify("warning", "Message cannot be empty.");
    if (!recipientPayload.ok) return notify("warning", "Please select recipients.");

    setSending(true);
    try {
      const token = getAuthToken();
      if (!token) {
        notify("warning", "Token missing. Please login again.");
        return;
      }

      const { data } = await axios.post(
        `${API_BASE}/messaging/send`,
        {
          recipient: recipientPayload.recipient,
          deliveryType,
          message: message.trim(),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const entry = {
        _id: data?._id || Date.now(),
        type: deliveryType,
        recipient: data?.recipient || recipientPayload.label,
        status: data?.status || "Sent",
        message: message.trim(),
        createdAt: new Date().toISOString(),
      };
      setMessageHistory((prev) => [entry, ...prev]);

      notify("success", data?.msg || "Message sent successfully.");
      setMessage("");

      if (tab === 1) setSelectedIndividuals([]);
      if (tab === 2) setSelectedSubordinates([]);
    } catch (e) {
      console.error(e);
      notify("error", e?.response?.data?.msg || "Failed to send message.");
    } finally {
      setSending(false);
    }
  };

  const TopStat = ({ label, value, icon }) => (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 3,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        flexWrap: "wrap",
        bgcolor: alpha(NAV_THEME_PANEL_ALT, 0.96),
        borderColor: NAV_THEME_BORDER,
        color: NAV_THEME_TEXT,
        minHeight: 110,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Avatar
          sx={{
            width: 46,
            height: 46,
            bgcolor: alpha("#ffffff", 0.14),
            color: NAV_THEME_TEXT,
            border: `1px solid ${NAV_THEME_BORDER}`,
          }}
        >
          {icon}
        </Avatar>
        <Box>
          <Typography variant="body2" sx={{ color: NAV_THEME_MUTED, fontWeight: 500 }}>
            {label}
          </Typography>
          <Typography variant="h5" sx={{ lineHeight: 1.05, fontWeight: 800, color: NAV_THEME_TEXT }}>
            {value}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        p: { xs: 1.5, md: 3 },
        bgcolor: "#eef4ff",
        backgroundImage:
          "radial-gradient(circle at top left, rgba(78,161,255,0.14), transparent 28%), radial-gradient(circle at top right, rgba(46,214,196,0.12), transparent 24%)",
      }}
    >
      {/* Header */}
      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          p: { xs: 2, md: 3.25 },
          borderRadius: 4,
          mb: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
          bgcolor: NAV_THEME_BG,
          color: NAV_THEME_TEXT,
          borderColor: alpha("#ffffff", 0.1),
          boxShadow: "0 24px 48px rgba(15, 23, 42, 0.18)",
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Avatar
            sx={{
              width: 56,
              height: 56,
              bgcolor: alpha("#ffffff", 0.14),
              color: NAV_THEME_TEXT,
              border: `1px solid ${alpha("#ffffff", 0.12)}`,
            }}
          >
            <MessageIcon />
          </Avatar>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, fontSize: { xs: "1.85rem", md: "2.15rem" } }}>
              Real-Time Message Management
            </Typography>
            <Typography variant="body1" sx={{ color: NAV_THEME_MUTED }}>
              Role broadcast • Multi-user • Hierarchy subordinates • Notification/SMS
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title={connected ? "Socket connected" : "Socket disconnected"}>
            <Chip
              icon={connected ? <WifiIcon /> : <WifiOffIcon />}
              label={connected ? "Live Connected" : "Disconnected"}
              variant="filled"
              sx={{
                height: 40,
                borderRadius: 999,
                px: 1,
                fontWeight: 800,
                color: "#fff",
                bgcolor: connected ? NAV_THEME_SUCCESS : alpha("#ffffff", 0.1),
                border: `1px solid ${connected ? "transparent" : alpha("#ffffff", 0.18)}`,
                "& .MuiChip-icon": { color: "#fff" },
              }}
            />
          </Tooltip>
        </Stack>
      </Paper>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <TopStat label="Users Loaded" value={loadingUsers ? "Loading..." : allUsers.length} icon={<PeopleIcon fontSize="small" />} />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <TopStat label="Recent History" value={messageHistory.length} icon={<MessageIcon fontSize="small" />} />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <TopStat
            label="Delivery"
            value={deliveryType}
            icon={deliveryType === "SMS" ? <SmsIcon fontSize="small" /> : <NotificationsIcon fontSize="small" />}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        {/* Composer */}
        <Grid item xs={12} lg={7}>
          <Paper variant="outlined" sx={panelSx}>
            <Box
              sx={{
                p: 2.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap",
                bgcolor: alpha("#ffffff", 0.03),
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <SendIcon sx={{ color: NAV_THEME_TEXT }} />
                <Typography variant="h5" sx={{ fontWeight: 800, color: NAV_THEME_TEXT }}>
                  Compose & Send
                </Typography>
              </Stack>

              <ToggleButtonGroup
                exclusive
                value={deliveryType}
                onChange={(_, v) => v && setDeliveryType(v)}
                size="small"
                sx={{
                  "& .MuiToggleButton-root": {
                    borderColor: NAV_THEME_BORDER,
                    color: NAV_THEME_MUTED,
                    px: 1.5,
                    py: 0.8,
                    fontWeight: 800,
                    bgcolor: alpha("#ffffff", 0.04),
                  },
                  "& .MuiToggleButton-root.Mui-selected": {
                    color: NAV_THEME_TEXT,
                    bgcolor: alpha(NAV_THEME_ACCENT, 0.18),
                    borderColor: alpha(NAV_THEME_ACCENT, 0.5),
                  },
                }}
              >
                {DELIVERY.map((d) => (
                  <ToggleButton key={d.value} value={d.value}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {d.icon}
                      <span>{d.label}</span>
                    </Stack>
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>

            <Divider />

            <Box sx={{ p: 2.5 }}>
              <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  mb: 2,
                  "& .MuiTab-root": {
                    color: NAV_THEME_MUTED,
                    fontWeight: 800,
                    minHeight: 52,
                  },
                  "& .Mui-selected": {
                    color: `${NAV_THEME_TEXT} !important`,
                  },
                  "& .MuiTabs-indicator": {
                    backgroundColor: NAV_THEME_ACCENT,
                    height: 3,
                    borderRadius: 99,
                  },
                }}
              >
                {availableTabs.includes(0) && <Tab value={0} label="By Role" />}
                {availableTabs.includes(1) && <Tab value={1} label="Individuals (Multi-select)" />}
                {availableTabs.includes(2) && <Tab value={2} label="Subordinates (Hierarchy)" />}
              </Tabs>

              {/* Role */}
              <RecipientTabPanel value={tab} index={0}>
                <Stack spacing={2} sx={formShellSx}>
                  <Alert severity="info" variant="outlined" sx={{ color: NAV_THEME_TEXT, borderColor: alpha(NAV_THEME_ACCENT, 0.45), bgcolor: alpha(NAV_THEME_ACCENT, 0.1) }}>
                    Select a role and send to all active users in that role.
                  </Alert>

                  <FormControl fullWidth>
                    <InputLabel id="role-label">Target Role</InputLabel>
                    <Select labelId="role-label" label="Target Role" value={selectedRole} onChange={(e) => setSelectedRole(e.target.value)}>
                      {availableRoleOptions.map((r) => (
                        <MenuItem key={r.value} value={r.value}>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ width: "100%" }}>
                            <Typography sx={{ fontWeight: 700 }}>{r.label}</Typography>
                            {r.canHaveSubordinates && <Chip size="small" label="Has hierarchy" variant="outlined" />}
                          </Stack>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      display: "flex",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 1,
                      bgcolor: alpha("#ffffff", 0.03),
                      borderColor: NAV_THEME_BORDER,
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 900, color: NAV_THEME_TEXT }}>
                        Selected Audience
                      </Typography>
                      <Typography variant="body2" sx={{ color: NAV_THEME_MUTED }}>
                        {recipientPayload.label || "-"}
                      </Typography>
                    </Box>
                    <Chip
                      label="Broadcast"
                      variant="outlined"
                      sx={{ color: NAV_THEME_TEXT, borderColor: alpha(NAV_THEME_ACCENT, 0.45), bgcolor: alpha(NAV_THEME_ACCENT, 0.12) }}
                    />
                  </Paper>
                </Stack>
              </RecipientTabPanel>

              {/* Individuals */}
              <RecipientTabPanel value={tab} index={1}>
                <Stack spacing={2} sx={formShellSx}>
                  <Alert severity="info" variant="outlined" sx={{ color: NAV_THEME_TEXT, borderColor: alpha(NAV_THEME_ACCENT, 0.45), bgcolor: alpha(NAV_THEME_ACCENT, 0.1) }}>
                    {isScopedSender
                      ? "Only users inside your own hierarchy are available here."
                      : "Search users and select multiple recipients. (Backend: will send as multi-users payload)"}
                  </Alert>

                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={individualOptions}
                    value={selectedIndividuals}
                    loading={loadingUsers}
                    onChange={(_, value) => setSelectedIndividuals(value)}
                    inputValue={individualSearch}
                    onInputChange={(_, v) => setIndividualSearch(v)}
                    getOptionLabel={(opt) => `${opt.username || "User"}${opt.email ? ` • ${opt.email}` : ""}`}
                    isOptionEqualToValue={(a, b) => a._id === b._id}
                    renderOption={(props, option, { selected }) => (
                      <li {...props} key={option._id}>
                        <Checkbox checked={selected} sx={{ mr: 1 }} />
                        <Stack sx={{ flex: 1 }}>
                          <Typography sx={{ fontWeight: 700 }}>{option.username || "User"}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.email || "-"} • {option.role || "-"}
                          </Typography>
                        </Stack>
                        <Chip size="small" label={option.role || "role"} variant="outlined" />
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Search users (name/email)"
                        placeholder="Type to search..."
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
                              {params.InputProps.startAdornment}
                            </>
                          ),
                          endAdornment: (
                            <>
                              {loadingUsers ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />

                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      display: "flex",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 1,
                      bgcolor: alpha("#ffffff", 0.03),
                      borderColor: NAV_THEME_BORDER,
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 900, color: NAV_THEME_TEXT }}>
                        Selected Users
                      </Typography>
                      <Typography variant="body2" sx={{ color: NAV_THEME_MUTED }}>
                        {selectedIndividuals.length} selected
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {selectedIndividuals.slice(0, 5).map((u) => (
                        <Chip key={u._id} label={u.username} size="small" />
                      ))}
                      {selectedIndividuals.length > 5 && (
                        <Chip size="small" label={`+${selectedIndividuals.length - 5} more`} variant="outlined" />
                      )}
                    </Stack>
                  </Paper>
                </Stack>
              </RecipientTabPanel>

              {/* Subordinates */}
              <RecipientTabPanel value={tab} index={2}>
                <Stack spacing={2} sx={formShellSx}>
                  <Alert severity="info" variant="outlined" sx={{ color: NAV_THEME_TEXT, borderColor: alpha(NAV_THEME_ACCENT, 0.45), bgcolor: alpha(NAV_THEME_ACCENT, 0.1) }}>
                    Select parent role → select parent user → multi-select subordinates.
                  </Alert>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel id="sub-role">Step 1: Role Type</InputLabel>
                        <Select
                          labelId="sub-role"
                          label="Step 1: Role Type"
                          value={selectedRole}
                          onChange={(e) => {
                            setSelectedRole(e.target.value);
                            setSelectedRoleUser(null);
                            setRoleUserSearch("");
                            setSubordinateUsers([]);
                            setSelectedSubordinates([]);
                          }}
                        >
                          {availableRoleOptions.filter((r) => r.canHaveSubordinates).map((r) => (
                            <MenuItem key={r.value} value={r.value}>
                              {r.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Autocomplete
                        options={roleUserOptions}
                        value={selectedRoleUser}
                        loading={loadingRoleUsers}
                        inputValue={roleUserSearch}
                        onInputChange={(_, v) => setRoleUserSearch(v)}
                        onChange={(_, v) => {
                          setSelectedRoleUser(v);
                          setSelectedSubordinates([]);
                          setSubSearch("");
                        }}
                        getOptionLabel={(opt) => `${opt.username || "User"}${opt.email ? ` • ${opt.email}` : ""}`}
                        isOptionEqualToValue={(a, b) => a._id === b._id}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={`Step 2: Select Parent (${selectedRoleConfig?.label?.replace(/s$/, "") || "User"})`}
                            placeholder={loadingRoleUsers ? "Loading..." : "Search by name/email..."}
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <>
                                  <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
                                  {params.InputProps.startAdornment}
                                </>
                              ),
                              endAdornment: (
                                <>
                                  {loadingRoleUsers ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>

                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      bgcolor: alpha("#ffffff", 0.03),
                      borderColor: NAV_THEME_BORDER,
                    }}
                  >
                    <Stack spacing={1.5}>
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between" flexWrap="wrap">
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 900, color: NAV_THEME_TEXT }}>
                            Step 3: Select Subordinates
                          </Typography>
                          <Typography variant="body2" sx={{ color: NAV_THEME_MUTED }}>
                            {selectedRoleUser ? `Under ${selectedRoleUser.username} • ${subordinateUsers.length} available` : "Select a parent user first."}
                          </Typography>
                        </Box>

                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<DoneAllIcon />}
                            onClick={selectAllSubordinates}
                            disabled={!selectedRoleUser || loadingSubs || filteredSubs.length === 0}
                          >
                            Select All
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<ClearAllIcon />}
                            onClick={clearAllSubordinates}
                            disabled={selectedSubordinates.length === 0}
                          >
                            Clear
                          </Button>
                        </Stack>
                      </Stack>

                      <TextField
                        value={subSearch}
                        onChange={(e) => setSubSearch(e.target.value)}
                        label="Search subordinates"
                        placeholder="Search by name/email..."
                        InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} /> }}
                        disabled={!selectedRoleUser || loadingSubs}
                        fullWidth
                      />

                      {loadingSubs ? (
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 2 }}>
                          <CircularProgress size={20} />
                          <Typography variant="body2" sx={{ color: NAV_THEME_MUTED }}>
                            Loading subordinates...
                          </Typography>
                        </Stack>
                      ) : !selectedRoleUser ? (
                        <Alert severity="warning" variant="outlined" sx={{ color: NAV_THEME_TEXT }}>
                          Select a parent user (Step 2).
                        </Alert>
                      ) : subordinateUsers.length === 0 ? (
                        <Alert severity="info" variant="outlined" sx={{ color: NAV_THEME_TEXT, borderColor: alpha(NAV_THEME_ACCENT, 0.45), bgcolor: alpha(NAV_THEME_ACCENT, 0.1) }}>
                          No subordinates found.
                        </Alert>
                      ) : (
                        <Autocomplete
                          multiple
                          disableCloseOnSelect
                          options={filteredSubs}
                          value={selectedSubordinates}
                          onChange={(_, value) => setSelectedSubordinates(value)}
                          getOptionLabel={(opt) => `${opt.username || "User"}${opt.email ? ` • ${opt.email}` : ""}`}
                          isOptionEqualToValue={(a, b) => a._id === b._id}
                          renderOption={(props, option, { selected }) => (
                            <li {...props} key={option._id}>
                              <Checkbox checked={selected} sx={{ mr: 1 }} />
                              <Stack sx={{ flex: 1 }}>
                                <Typography sx={{ fontWeight: 700 }}>{option.username || "User"}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {option.email || "-"} • {option.role || "-"}
                                </Typography>
                              </Stack>
                              <Chip size="small" label={option.role || "role"} variant="outlined" />
                            </li>
                          )}
                          renderInput={(params) => <TextField {...params} label="Selected subordinates" placeholder="Pick users..." />}
                        />
                      )}

                      {selectedSubordinates.length > 0 && (
                        <Alert severity="success" variant="outlined" sx={{ color: NAV_THEME_TEXT }}>
                          {selectedSubordinates.length} users selected.
                        </Alert>
                      )}
                    </Stack>
                  </Paper>
                </Stack>
              </RecipientTabPanel>

              {/* Composer */}
              <Divider sx={{ my: 2, borderColor: NAV_THEME_BORDER }} />

              <Stack spacing={1.5} sx={formShellSx}>
                <TextField
                  label="Message Content"
                  placeholder="Type message..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  multiline
                  minRows={4}
                  maxRows={10}
                  fullWidth
                  disabled={sending}
                  helperText={`${message.length}/1000`}
                />

                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    display: "flex",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 1,
                    bgcolor: alpha("#ffffff", 0.03),
                    borderColor: NAV_THEME_BORDER,
                  }}
                >
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 900, color: NAV_THEME_TEXT }}>
                      Ready to Send
                    </Typography>
                    <Typography variant="body2" sx={{ color: NAV_THEME_MUTED }}>
                      To: <b>{recipientPayload.label || "-"}</b> • Via: <b>{deliveryType}</b>
                    </Typography>
                  </Box>

                  <Button
                    variant="contained"
                    size="large"
                    startIcon={sending ? <CircularProgress size={18} /> : <SendIcon />}
                    onClick={handleSend}
                    disabled={!isFormValid || sending}
                    sx={{
                      borderRadius: 2.5,
                      px: 2.2,
                      fontWeight: 800,
                      textTransform: "none",
                      background: "linear-gradient(135deg, #4ea1ff 0%, #2d6cdf 100%)",
                      boxShadow: "0 10px 24px rgba(45,108,223,0.28)",
                    }}
                  >
                    {sending ? "Sending..." : `Send ${deliveryType}`}
                  </Button>
                </Paper>

                {deliveryType === "SMS" ? (
                  <Alert severity="warning" variant="outlined" sx={{ color: NAV_THEME_TEXT }}>
                    SMS integration required (Twilio/MSG91). Credits may be used.
                  </Alert>
                ) : (
                  <Alert severity="info" variant="outlined" sx={{ color: NAV_THEME_TEXT, borderColor: alpha(NAV_THEME_ACCENT, 0.45), bgcolor: alpha(NAV_THEME_ACCENT, 0.1) }}>
                    Notification is instant for online users & saved in DB for inbox.
                  </Alert>
                )}
              </Stack>
            </Box>
          </Paper>
        </Grid>

        {/* History */}
        <Grid item xs={12} lg={5}>
          <Paper variant="outlined" sx={panelSx}>
            <Box
              sx={{
                p: 2.5,
                display: "flex",
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap",
                bgcolor: alpha("#ffffff", 0.03),
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <MessageIcon sx={{ color: NAV_THEME_TEXT }} />
                <Typography variant="h5" sx={{ fontWeight: 800, color: NAV_THEME_TEXT }}>
                  History
                </Typography>
              </Stack>

              <Button
                size="small"
                startIcon={<RefreshIcon />}
                variant="outlined"
                onClick={fetchHistory}
                sx={{
                  borderRadius: 2.5,
                  borderColor: alpha(NAV_THEME_ACCENT, 0.45),
                  color: NAV_THEME_TEXT,
                  bgcolor: alpha("#ffffff", 0.03),
                }}
              >
                Refresh
              </Button>
            </Box>

            <Divider />

            <Box sx={{ p: 2.5 }}>
              {messageHistory.length === 0 ? (
                <Alert severity="info" variant="outlined" sx={{ color: NAV_THEME_TEXT, borderColor: alpha(NAV_THEME_ACCENT, 0.45), bgcolor: alpha(NAV_THEME_ACCENT, 0.1) }}>
                  No messages yet.
                </Alert>
              ) : (
                <TableContainer
                  sx={{
                    borderRadius: 3,
                    border: `1px solid ${NAV_THEME_BORDER}`,
                    bgcolor: alpha("#ffffff", 0.03),
                    overflowX: "auto",
                  }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: NAV_THEME_MUTED, borderColor: NAV_THEME_BORDER, fontWeight: 800 }}>Type</TableCell>
                        <TableCell sx={{ color: NAV_THEME_MUTED, borderColor: NAV_THEME_BORDER, fontWeight: 800 }}>Recipient</TableCell>
                        <TableCell sx={{ color: NAV_THEME_MUTED, borderColor: NAV_THEME_BORDER, fontWeight: 800 }}>Message</TableCell>
                        <TableCell sx={{ color: NAV_THEME_MUTED, borderColor: NAV_THEME_BORDER, fontWeight: 800 }}>Status</TableCell>
                        <TableCell sx={{ color: NAV_THEME_MUTED, borderColor: NAV_THEME_BORDER, fontWeight: 800 }}>Time</TableCell>
                        <TableCell sx={{ color: NAV_THEME_MUTED, borderColor: NAV_THEME_BORDER, fontWeight: 800, width: 72 }}>View</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {messageHistory.map((h) => {
                        const isSms = (h.type || "").toLowerCase() === "sms";
                        const status = (h.status || "sent").toLowerCase();
                        return (
                          <TableRow
                            key={h._id}
                            hover
                            sx={{
                              "&:hover": { bgcolor: alpha("#ffffff", 0.04) },
                            }}
                          >
                            <TableCell sx={{ borderColor: NAV_THEME_BORDER, color: NAV_THEME_TEXT }}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Avatar
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    bgcolor: alpha("#ffffff", 0.12),
                                    color: NAV_THEME_TEXT,
                                  }}
                                >
                                  {isSms ? <SmsIcon fontSize="small" /> : <NotificationsIcon fontSize="small" />}
                                </Avatar>
                                <Chip
                                  size="small"
                                  label={h.type || "Notification"}
                                  variant="outlined"
                                  sx={{
                                    color: NAV_THEME_TEXT,
                                    borderColor: isSms ? alpha("#e879f9", 0.45) : alpha(NAV_THEME_ACCENT, 0.45),
                                    bgcolor: isSms ? alpha("#e879f9", 0.12) : alpha(NAV_THEME_ACCENT, 0.12),
                                  }}
                                />
                              </Stack>
                            </TableCell>
                            <TableCell sx={{ borderColor: NAV_THEME_BORDER, color: NAV_THEME_TEXT, fontWeight: 700 }}>
                              {h.recipient || "-"}
                            </TableCell>
                            <TableCell sx={{ borderColor: NAV_THEME_BORDER, color: NAV_THEME_MUTED, maxWidth: 280 }}>
                              {String(h.message || "").length > 110 ? `${String(h.message).slice(0, 110)}…` : h.message}
                            </TableCell>
                            <TableCell sx={{ borderColor: NAV_THEME_BORDER }}>
                              <Chip
                                size="small"
                                label={h.status || "Sent"}
                                variant="outlined"
                                sx={{
                                  color: NAV_THEME_TEXT,
                                  borderColor:
                                    status === "sent"
                                      ? alpha("#4ade80", 0.45)
                                      : status === "failed"
                                      ? alpha("#f87171", 0.45)
                                      : alpha("#fbbf24", 0.45),
                                  bgcolor:
                                    status === "sent"
                                      ? alpha("#4ade80", 0.12)
                                      : status === "failed"
                                      ? alpha("#f87171", 0.12)
                                      : alpha("#fbbf24", 0.12),
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ borderColor: NAV_THEME_BORDER, color: NAV_THEME_MUTED, whiteSpace: "nowrap" }}>
                              {fmtDateTime(h.createdAt || h.date)}
                            </TableCell>
                            <TableCell sx={{ borderColor: NAV_THEME_BORDER }}>
                              <Tooltip title="Preview full message">
                                <IconButton
                                  size="small"
                                  onClick={() => notify("info", h.message || "")}
                                  sx={{
                                    color: NAV_THEME_TEXT,
                                    border: `1px solid ${NAV_THEME_BORDER}`,
                                    bgcolor: alpha("#ffffff", 0.03),
                                  }}
                                >
                                  <MessageIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          variant="filled"
          sx={{ borderRadius: 2 }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
